import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { fetchPlatformFeatures } from '../lib/queryFns';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type PlatformFeatureKey = 'resume-review' | 'culture-call' | 'referral-session';

export const ALL_PLATFORM_FEATURE_KEYS: PlatformFeatureKey[] = [
  'resume-review',
  'culture-call',
  'referral-session',
];

interface PlatformFeaturesContextType {
  /** Keys of features that are currently enabled. Defaults to all-enabled on error. */
  enabledFeatures: PlatformFeatureKey[];
  /** True while the initial fetch is in flight. */
  loading: boolean;
  /** Returns true if the given feature key is currently enabled. */
  isFeatureEnabled: (key: PlatformFeatureKey) => boolean;
  /** Manually re-fetch from Supabase (e.g. after admin changes). */
  refresh: () => Promise<void>;
}

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

const PlatformFeaturesContext = createContext<PlatformFeaturesContextType>({
  enabledFeatures: [...ALL_PLATFORM_FEATURE_KEYS],
  loading: false,
  isFeatureEnabled: () => true,
  refresh: async () => {},
});

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export const PlatformFeaturesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();

  const { data: rawFeatures, isLoading: loading } = useQuery({
    queryKey: queryKeys.platform.features(),
    queryFn: fetchPlatformFeatures,
    staleTime: 30 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });

  // Fail-open: default to all features enabled so a network/DB hiccup never hides services
  const enabledFeatures = useMemo<PlatformFeatureKey[]>(() => {
    if (!rawFeatures || rawFeatures.length === 0) {
      return [...ALL_PLATFORM_FEATURE_KEYS];
    }
    const enabled = rawFeatures
      .filter((row: { feature_key: string; enabled: boolean }) => row.enabled)
      .map((row: { feature_key: string; enabled: boolean }) => row.feature_key as PlatformFeatureKey);
    return enabled.length > 0 ? enabled : [...ALL_PLATFORM_FEATURE_KEYS];
  }, [rawFeatures]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.platform.features() });
  }, [queryClient]);

  const isFeatureEnabled = useCallback(
    (key: PlatformFeatureKey) => enabledFeatures.includes(key),
    [enabledFeatures],
  );

  return (
    <PlatformFeaturesContext.Provider
      value={{ enabledFeatures, loading, isFeatureEnabled, refresh }}
    >
      {children}
    </PlatformFeaturesContext.Provider>
  );
};

/* ------------------------------------------------------------------ */
/*  Hooks                                                              */
/* ------------------------------------------------------------------ */

export function usePlatformFeatures(): PlatformFeaturesContextType {
  return useContext(PlatformFeaturesContext);
}

export function useIsFeatureEnabled(key: PlatformFeatureKey): boolean {
  const { isFeatureEnabled } = usePlatformFeatures();
  return isFeatureEnabled(key);
}

export default PlatformFeaturesContext;
